import React, { useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { useConfig } from '../../contexts/ConfigContext';
import { useIframeCommunication } from '../../hooks/useIframeCommunication';
import { ArrowLeft } from 'lucide-react';
import GroupsList from '../../components/GroupsList';
import { formatHours, formatMoney, lessonTypeLabel, assessmentLabel, curriculumKindLabel } from '../../utils/directionCurriculum';
import { getDirectionTerms } from '../../utils/organizationHelpers';
import Footer from '../../components/Footer';
import './DirectionPage.css';

const DirectionPage = () => {
    useIframeCommunication();
    const { id } = useParams();
    const navigate = useNavigate();
    const { config } = useConfig();
    const orgCode = config?.code || 'mag';
    const directionTerms = getDirectionTerms(orgCode);
    const [selectedRegion, setSelectedRegion] = useState(null);

    const handleBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate('/public/groups');
        }
    };

    // Загрузка направления
    const { data: direction, isLoading: directionLoading } = useQuery(
        ['direction', id],
        () => axios.get(`${API_BASE_URL}/directions/${id}`).then(res => res.data),
        {
            retry: 1,
            refetchOnWindowFocus: false
        }
    );

    // Загрузка групп этого направления
    const { data: allGroupsRaw, isLoading: groupsLoading } = useQuery(
        ['directionGroups', id],
        () => {
            const params = new URLSearchParams();
            params.append('direction_id', id);

            return axios.get(`${API_BASE_URL}/groups/public?${params}`).then(res => res.data);
        },
        {
            enabled: !!id,
            retry: 1
        }
    );

    // Загрузка регионов для фильтра
    const { data: allRegions } = useQuery('publicRegions', () =>
        axios.get(`${API_BASE_URL}/regions/public`).then(res => res.data)
    );

    // Фильтрация групп
    const allGroups = useMemo(() => {
        if (!allGroupsRaw) return [];

        // Фильтруем только группы с открытым набором и донабором, с расписанием
        return allGroupsRaw.filter(group => {
            const status = group.recruitment_status;
            if (status !== 'open' && status !== 'started_additional') return false;
            const hasRegularFormat = group.format_data?.[0]?.frequency === 'weekly' || group.format_data?.[0]?.frequency === 'biweekly';
            return group.earliest_event_date || hasRegularFormat;
        });
    }, [allGroupsRaw]);

    // Фильтрация по региону
    const filteredGroups = useMemo(() => {
        if (!allGroups) return allGroups;
        if (!selectedRegion) return allGroups;

        return allGroups.filter(group => group.region_id === selectedRegion);
    }, [allGroups, selectedRegion]);

    // Уникальные регионы для фильтра
    const displayRegions = useMemo(() => {
        if (!allGroups || !allRegions) return [];

        const regionIds = new Set(allGroups.map(g => g.region_id).filter(Boolean));
        return allRegions.filter(r => regionIds.has(r.id));
    }, [allGroups, allRegions]);

    const handleRegionClick = (regionId) => {
        setSelectedRegion(selectedRegion === regionId ? null : regionId);
    };

    if (directionLoading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    if (!direction) {
        return (
            <div className="error-page">
                <h2>{directionTerms.notFound}</h2>
                <Link to="/directions" className="btn btn-primary">
                    Вернуться к списку {directionTerms.manyGen}
                </Link>
            </div>
        );
    }

    return (
        <div className="direction-page">
            <div className="direction-header">
                <button type="button" className="back-link" onClick={handleBack}>
                    <ArrowLeft size={20} />
                    Назад
                </button>

                <h1>{direction.full_name || direction.name}</h1>

                {direction.page_link && (() => {
                    const link = (direction.page_link || '').trim().replace(/\/+$/, '');
                    const orgWebsite = (config?.website || '').trim().replace(/\/+$/, '');
                    const isRootOrgWebsite = orgWebsite && (link === orgWebsite || link === orgWebsite.replace(/^https:/, 'http:'));
                    if (isRootOrgWebsite) return null;
                    return (
                        <div className="direction-full-description-link">
                            <a
                                href={direction.page_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-primary direction-full-description-btn"
                            >
                                Полное описание {directionTerms.oneGen}
                            </a>
                        </div>
                    );
                })()}
            </div>

            {direction.description && (
                <div className="direction-description-section">
                    <div className="direction-description">
                        {direction.description.split('\n').map((paragraph, idx) => (
                            <p key={idx}>{paragraph}</p>
                        ))}
                    </div>
                </div>
            )}

            {direction.themes?.length > 0 && (
                <div className="direction-curriculum">
                    <h2>Темы</h2>
                    <p className="direction-curriculum-meta">
                        {[
                            curriculumKindLabel(direction.curriculum_kind),
                            direction.hours_count != null && `${formatHours(direction.hours_count)} ч`,
                            assessmentLabel(direction.assessment_type),
                            formatMoney(direction.installment_cost) && `рассрочка ${formatMoney(direction.installment_cost)}`,
                            formatMoney(direction.full_payment_cost) && `полная оплата ${formatMoney(direction.full_payment_cost)}`
                        ].filter(Boolean).join(' · ')}
                    </p>
                    <table className="direction-themes">
                        <thead>
                            <tr>
                                <th>№</th>
                                <th>Тема</th>
                                <th>Часы</th>
                                <th>Вид</th>
                            </tr>
                        </thead>
                        <tbody>
                            {direction.themes.map((theme) => (
                                <tr key={theme.id || theme.position}>
                                    <td>{theme.position}</td>
                                    <td>
                                        {theme.title}
                                        {theme.with_attestation && (
                                            <span className="direction-theme-attest">
                                                {assessmentLabel(theme.attestation_type) || 'Аттестация'}
                                            </span>
                                        )}
                                    </td>
                                    <td>{formatHours(theme.hours_count)}</td>
                                    <td>{lessonTypeLabel(theme.lesson_type)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Фильтры - как на странице доступных групп */}
            {displayRegions.length > 0 && (
                <div className="filter-buttons">
                    <h4>Место проведения группы:</h4>
                    <br />
                    <div className="button-row">
                        {displayRegions.map(region => (
                            <button
                                key={region.id}
                                className={selectedRegion === region.id ? 'active-filter' : ''}
                                onClick={() => handleRegionClick(region.id)}
                            >
                                {region.name}
                            </button>
                        ))}
                    </div>
                    <br />
                    <hr width="100%" />
                    <br />
                </div>
            )}

            {/* Группы - как на странице доступных групп */}
            <GroupsList groups={filteredGroups} loading={groupsLoading} orgCode={orgCode} />
            <Footer />
        </div>
    );
};

export default DirectionPage;

